import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { IconArrowLeft, IconAlertCircle } from "@tabler/icons-react";
import type { ProductDto, ProductVariantDto } from "../../../../api";
import { ChangeRequestDtoStatusEnum } from "../../../../api";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Input } from "../../../../components/common/input";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { SectionCard } from "../../../../components/common/section-card";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useProductVariants } from "../../hooks/useProduct";
import { useCategories } from "../../hooks/useCategories";
import { useCategoryFieldConfig } from "../../hooks/useCategoryFieldConfig";
import { useCountries } from "../../hooks/useCountries";
import { StyledSelect } from "../components/styled-select";
import { useChangeRequestDetails } from "../../../change-requests/hooks/useChangeRequestDetails";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { EditWarrantySection } from "../components/edit-warranty-section";
import { CompatibleWarrantiesSection } from "../components/compatible-warranties-section";
import {
  EditVariantsSection,
  type EditVariantRow,
  type NewVariantDraft,
} from "../components/edit-variants-section";
import { useProductAttributeLines } from "../../hooks/useProductAttributeLines";
import type {
  WarrantyDisplayRow,
  NewWarrantyInput,
} from "../components/details-warranty-section";
import { ImageDropzone } from "../components/image-dropzone";
import { ProductNotFound } from "../components/product-not-found";
import {
  uploadProductImage,
  ProductImageUploadError,
} from "../../utils/upload-product-image";
import { changeRequestsService } from "../../../api_client";

/**
 * Shape of `proposedChanges` for PRODUCT_UPDATE change requests — mirrors
 * `UpdateProductDto` on the BE. We use it to hydrate the form in edit-existing-CR
 * mode (`?cr=<id>`) so the seller picks up exactly what they had previously
 * proposed, not the product's current state.
 */
type ProductUpdateProposedChanges = {
  name?: string;
  description?: string;
  countryOfOriginId?: number;
  categoryId?: number;
  compatibleWarrantyIds?: number[];
  variants?: Array<{
    id: number;
    quantity?: number;
    sellerPrice?: number;
    priceExtra?: number;
    image?: string;
  }>;
  newVariants?: Array<{
    attributeValues: Array<{
      attributeId: number;
      valueId?: number;
      newValueName?: string;
    }>;
    sku?: string;
    sellerPrice?: number;
    quantity?: number;
    image?: string;
    discountPercentage?: number;
  }>;
  discounts?: Array<{ variantId: number; discountPercentage: number }>;
};

/**
 * Per-preview metadata aligned with the dropzone's `images: string[]`.
 *
 * - `kind: "main"` — the product.template.image_1920. There is exactly one
 *   of these and it lives at index 0. If the user replaces it, `file` is set.
 * - `kind: "existing-gallery"` — a product.image row already on the server;
 *   `id` identifies it in the reconcile call (keepIds).
 * - `kind: "new"` — a freshly added file the seller just dropped in.
 */
type ImageMeta =
  | { kind: "main"; file?: File }
  | { kind: "existing-gallery"; id: number }
  | { kind: "new"; file: File };

function deriveInitialImages(
  product: ProductDto,
): { previews: string[]; meta: ImageMeta[] } {
  const previews: string[] = [];
  const meta: ImageMeta[] = [];
  if (product.image) {
    previews.push(product.image);
    meta.push({ kind: "main" });
  }
  const gallery = (product as unknown as {
    galleryImages?: string[];
    galleryImageIds?: number[];
  });
  const galleryImages = gallery.galleryImages ?? [];
  const galleryIds = gallery.galleryImageIds ?? [];
  for (let i = 0; i < galleryImages.length; i++) {
    const id = galleryIds[i];
    if (typeof id !== "number") continue;
    previews.push(galleryImages[i]);
    meta.push({ kind: "existing-gallery", id });
  }
  return { previews, meta };
}

function deriveTemplateWarranty(product: ProductDto): WarrantyDisplayRow[] {
  if (!product.warranty?.name && !product.warrantyType?.name) return [];
  return [
    {
      id: 1,
      period: product.warranty?.name ?? "",
      type: product.warrantyType?.name ?? "",
      provider: "Merchant warranty",
    },
  ];
}

function variantToEditRow(v: ProductVariantDto): EditVariantRow {
  return {
    id: v.id,
    variant: v.displayName ?? v.name ?? `Variant #${v.id}`,
    sku: v.defaultCode ?? "",
    price: String(v.sellerPrice ?? (v.listPrice ?? 0) + (v.priceExtra ?? 0)),
    stock: String(v.supplierStock ?? 0),
    discount: String(v.discountPercentage ?? 0),
    imageUrl: v.image ?? undefined,
  };
}

export function MyProductEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const productId = Number(id ?? 0);
  const [searchParams] = useSearchParams();
  const editingCrId = searchParams.get("cr") || null;

  const { data, isLoading, error } = useProductVariants(productId);
  // Only load the CR if we're in edit-existing-CR mode. The hook no-ops when id is null.
  const { data: editingCr, isLoading: isCrLoading } = useChangeRequestDetails(
    editingCrId ?? undefined,
  );

  // Treat the CR as editable only while it's in CHANGES_REQUESTED — once it's
  // back to PENDING / APPROVED / etc. the seller should not be able to mutate
  // it via this page. The BE enforces this too, but we hide the form to avoid
  // building a payload the BE will reject.
  const canEditCr =
    !editingCrId ||
    (editingCr &&
      editingCr.status === ChangeRequestDtoStatusEnum.ChangesRequested);

  const proposed = useMemo<ProductUpdateProposedChanges>(
    () =>
      (editingCr?.proposedChanges as ProductUpdateProposedChanges | undefined) ??
      {},
    [editingCr?.proposedChanges],
  );

  const initialVariants = useMemo<EditVariantRow[]>(
    () => {
      const rows = (data?.variants ?? []).map(variantToEditRow);
      if (!editingCrId) return rows;
      // Overlay proposed variant fields on top of the live product so the
      // seller's previously-submitted edits are what they see in the form.
      const proposedVarsById = new Map(
        (proposed.variants ?? []).map((v) => [v.id, v]),
      );
      const proposedDiscountById = new Map(
        (proposed.discounts ?? []).map((d) => [d.variantId, d.discountPercentage]),
      );
      return rows.map((row) => {
        const v = proposedVarsById.get(row.id);
        const d = proposedDiscountById.get(row.id);
        return {
          ...row,
          ...(v?.quantity !== undefined ? { stock: String(v.quantity) } : {}),
          ...(v?.sellerPrice !== undefined
            ? { price: String(v.sellerPrice) }
            : {}),
          ...(d !== undefined ? { discount: String(d) } : {}),
        };
      });
    },
    [data?.variants, editingCrId, proposed],
  );
  const initialWarranties = useMemo<WarrantyDisplayRow[]>(
    () => (data?.product ? deriveTemplateWarranty(data.product) : []),
    [data?.product],
  );
  const initialImagePair = useMemo(
    () =>
      data?.product
        ? deriveInitialImages(data.product)
        : { previews: [] as string[], meta: [] as ImageMeta[] },
    [data?.product],
  );

  const [name, setName]               = useState("");
  // Category and country are now stored as Odoo IDs (stringified, to match
  // the new-product page's StyledSelect contract). They round-trip through
  // the patch as numeric `categoryId` / `countryOfOriginId`.
  const [categoryId, setCategoryId]   = useState("");
  const [sku, setSku]                 = useState("");
  const [countryOfOriginId, setCountryOfOriginId] = useState("");
  const [description, setDescription] = useState("");

  const { data: categories, isLoading: categoriesLoading } = useCategories();
  const { data: countries, isLoading: countriesLoading } = useCountries();
  // Per-category field policy. On edit only the warranty section is a
  // policy-governed surface that's rendered here (spec fields like screen size
  // aren't shown on this page), so it's the one we gate.
  const { config: fieldPolicy } = useCategoryFieldConfig(
    Number.parseInt(categoryId, 10),
  );
  const categoryOptions = useMemo(
    () =>
      (categories ?? []).map((c) => ({
        value: String(c.id),
        label: c.completeName ?? c.displayName ?? c.name,
      })),
    [categories],
  );
  const countryOptions = useMemo(
    () => (countries ?? []).map((c) => ({ value: String(c.id), label: c.name })),
    [countries],
  );
  const [images, setImages]           = useState<string[]>([]);
  const [imageMeta, setImageMeta]     = useState<ImageMeta[]>([]);
  const [templateUploadProgress, setTemplateUploadProgress] = useState<number | undefined>();
  const [variants, setVariants]       = useState<EditVariantRow[]>([]);
  const [newVariants, setNewVariants] = useState<NewVariantDraft[]>([]);
  const { data: attributeLines } = useProductAttributeLines(productId);
  const [warranties, setWarranties]   = useState<WarrantyDisplayRow[]>([]);
  const [newWarranty, setNewWarranty] = useState<NewWarrantyInput | null>(null);
  const [compatibleWarrantyIds, setCompatibleWarrantyIds] = useState<number[]>([]);
  const [hydrated, setHydrated]       = useState(false);
  const [submitting, setSubmitting]   = useState(false);

  // Hydrate form state once when the API response lands. We avoid `data` in
  // deps so user edits aren't blown away by background SWR revalidation.
  // In edit-existing-CR mode (?cr=<id>) we also wait for the CR to load and
  // overlay its proposedChanges on top of the live product so the seller picks
  // up exactly what they had previously submitted.
  useEffect(() => {
    if (!data?.product || hydrated) return;
    if (editingCrId && !editingCr) return;
    // Carry over any newVariants from the proposed payload so a resubmit
    // doesn't lose them. We rebuild draftId locally — proposed entries don't
    // have one.
    if (editingCrId && proposed.newVariants?.length) {
      setNewVariants(
        proposed.newVariants.map((nv, i) => ({
          draftId: `proposed-${i}`,
          axisChoices: Object.fromEntries(
            nv.attributeValues.map((av) => [
              av.attributeId,
              av.valueId != null
                ? { valueId: av.valueId }
                : { newValueName: av.newValueName ?? "" },
            ]),
          ),
          sku: nv.sku ?? "",
          price: nv.sellerPrice != null ? String(nv.sellerPrice) : "",
          stock: nv.quantity != null ? String(nv.quantity) : "",
          discount: nv.discountPercentage != null ? String(nv.discountPercentage) : "",
        })),
      );
    }
    setName(proposed.name ?? data.product.displayName ?? data.product.name ?? "");
    setCategoryId(
      proposed.categoryId != null
        ? String(proposed.categoryId)
        : data.product.category?.id != null
          ? String(data.product.category.id)
          : "",
    );
    setSku(data.product.defaultCode ?? "");
    setCountryOfOriginId(
      proposed.countryOfOriginId != null
        ? String(proposed.countryOfOriginId)
        : data.product.countryOfOrigin?.id != null
          ? String(data.product.countryOfOrigin.id)
          : "",
    );
    setDescription(proposed.description ?? data.product.description ?? "");
    setImages(initialImagePair.previews);
    setImageMeta(initialImagePair.meta);
    setVariants(initialVariants);
    setWarranties(initialWarranties);
    setCompatibleWarrantyIds(
      proposed.compatibleWarrantyIds ??
        data.product.compatibleWarrantyIds ??
        [],
    );
    setHydrated(true);
  }, [
    data?.product,
    hydrated,
    initialImagePair,
    initialVariants,
    initialWarranties,
    editingCrId,
    editingCr,
    proposed,
  ]);

  function handleSaveWarranty() {
    if (!newWarranty) return;
    setWarranties((prev) => [...prev, { id: Date.now(), ...newWarranty }]);
    setNewWarranty(null);
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      // 1. Upload any staged files in parallel so we have GCS object keys
      // to send to the backend. We index by meta position so we can match
      // them back up after the upload finishes, since position 0 may have
      // been reordered to a non-main slot or vice versa.
      const uploadIndexes: number[] = [];
      const filesToUpload: File[] = [];
      imageMeta.forEach((m, i) => {
        if ((m.kind === "main" && m.file) || m.kind === "new") {
          uploadIndexes.push(i);
          filesToUpload.push(
            (m as { file?: File }).file as File,
          );
        }
      });
      const uploadedKeyByMetaIndex = new Map<number, string>();
      if (filesToUpload.length > 0) {
        try {
          setTemplateUploadProgress(0);
          const total = filesToUpload.length;
          let completed = 0;
          const keys = await Promise.all(
            filesToUpload.map((file) =>
              uploadProductImage(file, {
                onProgress: (percent) => {
                  setTemplateUploadProgress(
                    Math.round((completed * 100 + percent) / total),
                  );
                },
              }).then((key) => {
                completed += 1;
                return key;
              }),
            ),
          );
          uploadIndexes.forEach((idx, i) =>
            uploadedKeyByMetaIndex.set(idx, keys[i]),
          );
        } catch (err) {
          showErrorToast(
            err instanceof ProductImageUploadError
              ? `${err.message}. Edit was not submitted.`
              : "Image upload failed. Edit was not submitted.",
          );
          return;
        }
      }

      // 2. Position-based reconciliation. After drag-drop the seller may
      // have promoted a gallery row to position 0 (becomes the new main)
      // or demoted the previous main into the gallery. We resolve image
      // data per slot from one of three sources:
      //   - newly uploaded file → GCS key from uploadedKeyByMetaIndex
      //   - unchanged main → product.image base64 from the initial fetch
      //   - existing gallery row → its base64 from initial galleryImages
      // The backend accepts either a GCS key or raw base64 for both
      // `image` and `gallery.add[]`, so we pass the base64 through when
      // an existing image needs to land in a different slot.
      const initialMainBase64: string | undefined =
        (product.image as string | null | undefined) ?? undefined;
      const initialGalleryById = new Map<number, string>();
      const initialGalleryImages =
        (product as unknown as { galleryImages?: string[] }).galleryImages ?? [];
      const initialGalleryImageIds =
        (product as unknown as { galleryImageIds?: number[] }).galleryImageIds ?? [];
      for (let i = 0; i < initialGalleryImageIds.length; i++) {
        const id = initialGalleryImageIds[i];
        const b64 = initialGalleryImages[i];
        if (typeof id === "number" && typeof b64 === "string") {
          initialGalleryById.set(id, b64);
        }
      }

      function resolveImageValue(metaIdx: number, m: ImageMeta): string | undefined {
        if (m.kind === "main") {
          return m.file ? uploadedKeyByMetaIndex.get(metaIdx) : initialMainBase64;
        }
        if (m.kind === "new") return uploadedKeyByMetaIndex.get(metaIdx);
        return initialGalleryById.get(m.id);
      }

      // Position 0 → new main image. Only patch when it actually changed.
      let mainImagePatch: string | undefined;
      const newMainMeta = imageMeta[0];
      if (newMainMeta) {
        const mainUnchanged = newMainMeta.kind === "main" && !newMainMeta.file;
        if (!mainUnchanged) {
          mainImagePatch = resolveImageValue(0, newMainMeta);
        }
      }

      // Positions 1+ → gallery, in order. Existing gallery rows that
      // landed at position 0 are deliberately left out of keepIds so
      // reconcileGallery deletes the now-redundant row. The previous
      // main, if demoted, is sent as a new gallery entry (base64 or key).
      const keepIds: number[] = [];
      const galleryAdd: string[] = [];
      for (let i = 1; i < imageMeta.length; i++) {
        const m = imageMeta[i];
        if (m.kind === "existing-gallery") {
          keepIds.push(m.id);
          continue;
        }
        const value = resolveImageValue(i, m);
        if (value) galleryAdd.push(value);
      }

      // 3. Build the patch payload. Only include fields that meaningfully
      // changed so we don't no-op the rest into Odoo on every save.
      const patch: Record<string, unknown> = {};
      const initialName = product.displayName ?? product.name ?? "";
      const initialDescription = product.description ?? "";
      const initialCategoryId =
        product.category?.id != null ? String(product.category.id) : "";
      const initialCountryId =
        product.countryOfOrigin?.id != null ? String(product.countryOfOrigin.id) : "";
      if (name !== initialName) patch.name = name;
      if (description !== initialDescription) patch.description = description;
      if (categoryId !== initialCategoryId) {
        const asNum = Number.parseInt(categoryId, 10);
        if (Number.isFinite(asNum)) patch.categoryId = asNum;
      }
      if (countryOfOriginId !== initialCountryId) {
        const asNum = Number.parseInt(countryOfOriginId, 10);
        if (Number.isFinite(asNum)) patch.countryOfOriginId = asNum;
      }
      if (mainImagePatch) patch.image = mainImagePatch;
      const galleryChanged =
        galleryAdd.length > 0 ||
        keepIds.length !== initialGalleryImageIds.length ||
        keepIds.some((id, i) => id !== initialGalleryImageIds[i]);
      if (galleryChanged) {
        patch.gallery = { keepIds, add: galleryAdd };
      }

      // 4. Variants: emit one entry per variant whose stock, price, or image
      // changed, and a separate archiveVariantIds list for the ones the
      // seller removed. SKU is read-only in the UI so we don't diff it.
      // Variant image uploads happen in parallel before the PATCH.
      const variantsWithNewImage = variants.filter((v) => v.imageFile);
      let variantImageKeys = new Map<number, string>();
      if (variantsWithNewImage.length > 0) {
        try {
          const results = await Promise.all(
            variantsWithNewImage.map((v) =>
              uploadProductImage(v.imageFile!).then((key) => [v.id, key] as const),
            ),
          );
          variantImageKeys = new Map(results);
        } catch (err) {
          showErrorToast(
            err instanceof ProductImageUploadError
              ? `${err.message}. Edit was not submitted.`
              : "Variant image upload failed. Edit was not submitted.",
          );
          return;
        }
      }

      const initialById = new Map(initialVariants.map((v) => [v.id, v]));
      const variantUpdates: Array<{
        id: number;
        quantity?: number;
        sellerPrice?: number;
        image?: string;
      }> = [];
      for (const v of variants) {
        const original = initialById.get(v.id);
        if (!original) continue;
        const update: {
          id: number;
          quantity?: number;
          sellerPrice?: number;
          image?: string;
        } = { id: v.id };
        const newStock = Number.parseInt(v.stock, 10);
        const oldStock = Number.parseInt(original.stock, 10);
        if (
          Number.isFinite(newStock) &&
          (!Number.isFinite(oldStock) || newStock !== oldStock)
        ) {
          update.quantity = newStock;
        }
        const newPrice = Number.parseFloat(v.price);
        const oldPrice = Number.parseFloat(original.price);
        if (
          Number.isFinite(newPrice) &&
          (!Number.isFinite(oldPrice) || newPrice !== oldPrice)
        ) {
          // The form value is the seller's own list price (product.supplierinfo.price),
          // which is what the seller actually controls. Sending priceExtra here would
          // edit the buyer-facing variant delta while leaving the seller price
          // unchanged — confusing and wrong.
          update.sellerPrice = newPrice;
        }
        const imageKey = variantImageKeys.get(v.id);
        if (imageKey) update.image = imageKey;
        if (
          update.quantity !== undefined ||
          update.sellerPrice !== undefined ||
          update.image !== undefined
        ) {
          variantUpdates.push(update);
        }
      }
      if (variantUpdates.length > 0) patch.variants = variantUpdates;

      const liveIds = new Set(variants.map((v) => v.id));
      const archiveVariantIds = initialVariants
        .map((v) => v.id)
        .filter((id) => !liveIds.has(id));
      if (archiveVariantIds.length > 0) patch.archiveVariantIds = archiveVariantIds;

      const initialCompatibleWarrantyIds =
        ((product as unknown as { compatibleWarrantyIds?: number[] })
          .compatibleWarrantyIds) ?? [];
      const compatibleChanged =
        compatibleWarrantyIds.length !== initialCompatibleWarrantyIds.length ||
        [...compatibleWarrantyIds].sort().some(
          (id, i) => id !== [...initialCompatibleWarrantyIds].sort()[i],
        );
      if (compatibleChanged) {
        patch.compatibleWarrantyIds = compatibleWarrantyIds;
      }

      // Collect discount changes (submitted separately per variant)
      const discountUpdates: Array<{ id: number; discountPercentage: number }> = [];
      for (const v of variants) {
        const original = initialById.get(v.id);
        if (!original) continue;
        const newDiscount = Number.parseFloat(v.discount);
        const oldDiscount = Number.parseFloat(original.discount);
        if (
          Number.isFinite(newDiscount) &&
          (!Number.isFinite(oldDiscount) || newDiscount !== oldDiscount)
        ) {
          discountUpdates.push({ id: v.id, discountPercentage: newDiscount });
        }
      }

      // Fold discount updates into the same proposedChanges payload using the
      // BE UpdateProductDto.discounts shape ({ variantId, discountPercentage }).
      if (discountUpdates.length > 0) {
        patch.discounts = discountUpdates.map((u) => ({
          variantId: u.id,
          discountPercentage: u.discountPercentage,
        }));
      }

      // New variants — validate completeness, upload any staged images, then
      // pack into patch.newVariants. The BE adds new attribute values, lets
      // Odoo auto-generate the variant, then writes per-variant fields.
      if (newVariants.length > 0) {
        const requiredAxisIds = (attributeLines ?? []).map((l) => l.attributeId);
        const newVariantDtos: Array<{
          attributeValues: Array<{
            attributeId: number;
            valueId?: number;
            newValueName?: string;
          }>;
          sku?: string;
          sellerPrice?: number;
          quantity?: number;
          image?: string;
          discountPercentage?: number;
        }> = [];

        for (const draft of newVariants) {
          // Every axis must be covered, with either an existing value or a
          // non-empty new value name.
          const attributeValues: Array<{
            attributeId: number;
            valueId?: number;
            newValueName?: string;
          }> = [];
          let incomplete = false;
          for (const attrId of requiredAxisIds) {
            const choice = draft.axisChoices[attrId];
            if (!choice) {
              incomplete = true;
              break;
            }
            if (choice.valueId != null) {
              attributeValues.push({ attributeId: attrId, valueId: choice.valueId });
            } else if (choice.newValueName && choice.newValueName.trim().length > 0) {
              attributeValues.push({
                attributeId: attrId,
                newValueName: choice.newValueName.trim(),
              });
            } else {
              incomplete = true;
              break;
            }
          }
          if (incomplete) {
            showErrorToast(
              "Each new variant must pick a value for every attribute. Edit was not submitted.",
            );
            return;
          }

          // Upload the variant image (if any) before sending the patch.
          let imageKey: string | undefined;
          if (draft.imageFile) {
            try {
              imageKey = await uploadProductImage(draft.imageFile);
            } catch (err) {
              showErrorToast(
                err instanceof ProductImageUploadError
                  ? `${err.message}. Edit was not submitted.`
                  : "New-variant image upload failed. Edit was not submitted.",
              );
              return;
            }
          }

          const priceNum = Number.parseFloat(draft.price);
          const stockNum = Number.parseInt(draft.stock, 10);
          const discountNum = Number.parseFloat(draft.discount);

          newVariantDtos.push({
            attributeValues,
            sku: draft.sku.trim() || undefined,
            sellerPrice: Number.isFinite(priceNum) ? priceNum : undefined,
            quantity: Number.isFinite(stockNum) ? stockNum : undefined,
            image: imageKey,
            discountPercentage: Number.isFinite(discountNum) && discountNum > 0 ? discountNum : undefined,
          });
        }

        patch.newVariants = newVariantDtos;
      }

      if (Object.keys(patch).length === 0) {
        showSuccessToast("No changes to save.");
        navigate(detailsUrl);
        return;
      }

      // Seller edits go through the admin-reviewed change-request flow rather
      // than writing straight to Odoo. The BE applies the same proposedChanges
      // payload via productsService.update on approval.
      //
      // Two paths:
      //   - new CR (default): create a fresh request, then submit it
      //   - editing an existing CHANGES_REQUESTED CR (?cr=<id>): update its
      //     proposedChanges in place, then resubmit so it transitions back to
      //     PENDING. The BE re-snapshots on resubmit so the admin's diff stays
      //     accurate.
      try {
        if (editingCrId) {
          await changeRequestsService.update(editingCrId, {
            proposedChanges: patch as Record<string, unknown>,
          });
          await changeRequestsService.submit(editingCrId);
        } else {
          const created = await changeRequestsService.create({
            type: "PRODUCT_UPDATE",
            targetProductId: product.id,
            proposedChanges: patch as Record<string, unknown>,
          });
          if (created.data?.id) {
            await changeRequestsService.submit(created.data.id);
          }
        }
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string | string[] } } })
            ?.response?.data?.message;
        const flat = Array.isArray(msg) ? msg.join(", ") : msg;
        showErrorToast(flat || "Failed to submit product changes for review");
        return;
      }

      showSuccessToast(
        editingCrId
          ? "Change request resubmitted for admin review."
          : "Changes submitted for admin review.",
      );
      navigate(
        editingCrId
          ? AppRoutes.CHANGE_REQUEST_DETAILS.replace(":id", editingCrId)
          : detailsUrl,
      );
    } finally {
      setSubmitting(false);
      setTemplateUploadProgress(undefined);
    }
  }

  if (isLoading || (editingCrId && isCrLoading)) {
    return (
      <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:px-4 max-md:py-6">
        <ShimmerLoader
          message={editingCrId ? "Loading change request…" : "Loading product…"}
          lines={6}
        />
      </div>
    );
  }

  if (error || !data?.product) {
    return (
      <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:px-4 max-md:py-6">
        <ProductNotFound error={error} />
      </div>
    );
  }

  // If a CR was requested but isn't editable (already resubmitted, approved,
  // cancelled, etc.) we send the seller back to the CR detail so they don't
  // edit a no-op form. The BE would reject the update anyway.
  if (editingCrId && editingCr && !canEditCr) {
    return (
      <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:px-4 max-md:py-6">
        <Text variant="body" tone="body">
          This change request is no longer in "Changes requested" status and
          can't be edited. Open it{" "}
          <button
            type="button"
            className="underline"
            onClick={() =>
              navigate(
                AppRoutes.CHANGE_REQUEST_DETAILS.replace(":id", editingCrId),
              )
            }
          >
            from the change requests list
          </button>
          .
        </Text>
      </div>
    );
  }

  const product = data.product;
  const productName = product.displayName ?? product.name ?? "Product";
  const detailsUrl = AppRoutes.MY_PRODUCT_DETAILS.replace(":id", String(product.id));
  const crReviewNote = editingCr?.reviewNote ?? null;

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:px-4 max-md:py-6">

      <div className="flex items-center gap-2">
        <Button variant="ghostAccent" size="sm" leftIcon={<IconArrowLeft size={18} />}
          onClick={() =>
            navigate(
              editingCrId
                ? AppRoutes.CHANGE_REQUEST_DETAILS.replace(":id", editingCrId)
                : detailsUrl,
            )
          }
          className="!h-auto !px-0 !py-0">
          <Heading variant="h3" tone="inherit">
            {editingCrId ? "Back to change request" : "Back to Product"}
          </Heading>
        </Button>
        {/* Phones: the title right below already names the product. */}
        <Text variant="bodySm" tone="muted" className="max-md:hidden">/</Text>
        <Heading variant="h3" tone="dark" className="max-md:hidden">
          {editingCrId ? "Edit & resubmit" : "Edit"} - {productName}
        </Heading>
      </div>

      <Heading variant="h2" tone="dark">{productName}</Heading>

      {editingCrId && (
        <div className="flex items-start gap-3 rounded-2xl border border-borderDefault bg-bgWhite px-5 py-4">
          <IconAlertCircle size={20} className="mt-0.5 text-accentError shrink-0" />
          <div className="flex flex-col gap-1 min-w-0">
            <Text variant="bodyBold" tone="dark">
              Admin requested changes
            </Text>
            <Text variant="bodySm" tone="body" className="whitespace-pre-wrap">
              {crReviewNote || "No note was provided."}
            </Text>
            <Text variant="bodySm" tone="muted">
              Make the requested edits and click <b>Resubmit</b> at the bottom of
              this page.
            </Text>
          </div>
        </div>
      )}

      <SectionCard shadow title="General Product images" className="gap-5">
        <ImageDropzone
          images={images}
          onImagesChange={(next) => {
            // The dropzone emits two flavors of change: removals (next is
            // shorter) and reorders (next is the same length, items shuffled).
            // Both are handled by realigning meta to next by URL identity.
            setImages(next);
            setImageMeta((prev) => {
              const realigned: ImageMeta[] = [];
              const consumed = new Array<boolean>(prev.length).fill(false);
              for (const url of next) {
                const idx = images.findIndex((u, i) => u === url && !consumed[i]);
                if (idx === -1) continue;
                consumed[idx] = true;
                realigned.push(prev[idx]);
              }
              return realigned;
            });
          }}
          onFilesAdded={(files) => {
            // First newly added file replaces the main image when there
            // isn't one yet; subsequent ones become gallery additions.
            setImageMeta((prev) => {
              const next = [...prev];
              for (const file of files) {
                const hasMain = next.some((m) => m.kind === "main");
                if (!hasMain) {
                  next.unshift({ kind: "main", file });
                } else {
                  next.push({ kind: "new", file });
                }
              }
              return next;
            });
          }}
          uploadProgress={templateUploadProgress}
        />
      </SectionCard>

      <SectionCard shadow title="Basic information" className="gap-5">
        <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
          <Input label="Product name" value={name} onChange={(e) => setName(e.target.value)} />
          <StyledSelect
            label="Category"
            value={categoryId}
            onChange={setCategoryId}
            options={categoryOptions}
            placeholder="Select category"
            loading={categoriesLoading}
            searchable
          />
          <Input label="SKU" value={sku} onChange={(e) => setSku(e.target.value)} />
          <StyledSelect
            label="Country of origin"
            value={countryOfOriginId}
            onChange={setCountryOfOriginId}
            options={countryOptions}
            placeholder="Select country of origin"
            loading={countriesLoading}
            searchable
          />
          <div className="col-span-2 max-md:col-span-full">
            <Input label="Description" multiline rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>
      </SectionCard>

      {fieldPolicy.warranty.visible && (
        <EditWarrantySection
          warranties={warranties}
          newWarranty={newWarranty}
          onWarrantyRemove={(wid) => setWarranties((prev) => prev.filter((x) => x.id !== wid))}
          onWarrantyChange={(wid, field, value) => setWarranties((prev) => prev.map((x) => x.id === wid ? { ...x, [field]: value } : x))}
          onNewWarrantyChange={(patch) => setNewWarranty((prev) => prev ? { ...prev, ...patch } : prev)}
          onNewWarrantyStart={() => setNewWarranty({ period: "", type: "", provider: "" })}
          onNewWarrantySave={handleSaveWarranty}
          onNewWarrantyCancel={() => setNewWarranty(null)}
        />
      )}

      <CompatibleWarrantiesSection
        selectedIds={compatibleWarrantyIds}
        onChange={setCompatibleWarrantyIds}
      />

      <EditVariantsSection
        variants={variants}
        onVariantUpdate={(vid, field, value) => setVariants((prev) => prev.map((v) => v.id === vid ? { ...v, [field]: value } : v))}
        onVariantImageChange={(vid, file, previewUrl) =>
          setVariants((prev) =>
            prev.map((v) =>
              v.id === vid ? { ...v, imageFile: file, imageUrl: previewUrl } : v,
            ),
          )
        }
        onVariantRemove={(vid) => setVariants((prev) => prev.filter((v) => v.id !== vid))}
        attributeLines={attributeLines ?? []}
        newVariants={newVariants}
        onNewVariantAdd={() =>
          setNewVariants((prev) => [
            ...prev,
            {
              draftId:
                typeof crypto !== "undefined" && "randomUUID" in crypto
                  ? crypto.randomUUID()
                  : `draft-${Date.now()}-${prev.length}`,
              axisChoices: {},
              sku: "",
              price: "",
              stock: "",
              discount: "",
            },
          ])
        }
        onNewVariantUpdate={(draftId, patch) =>
          setNewVariants((prev) =>
            prev.map((d) => (d.draftId === draftId ? { ...d, ...patch } : d)),
          )
        }
        onNewVariantAxisChange={(draftId, attributeId, choice) =>
          setNewVariants((prev) =>
            prev.map((d) =>
              d.draftId === draftId
                ? {
                    ...d,
                    axisChoices: { ...d.axisChoices, [attributeId]: choice },
                  }
                : d,
            ),
          )
        }
        onNewVariantImageChange={(draftId, file, previewUrl) =>
          setNewVariants((prev) =>
            prev.map((d) =>
              d.draftId === draftId
                ? { ...d, imageFile: file, imageUrl: previewUrl }
                : d,
            ),
          )
        }
        onNewVariantRemove={(draftId) =>
          setNewVariants((prev) => prev.filter((d) => d.draftId !== draftId))
        }
      />

      <div className="flex justify-end gap-3 pb-4 max-md:[&>*]:flex-1">
        <Button
          variant="secondary"
          size="md"
          className="!px-8"
          onClick={() => navigate(detailsUrl)}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="md"
          className="!px-8"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting
            ? templateUploadProgress != null
              ? "Uploading image…"
              : editingCrId
                ? "Resubmitting…"
                : "Saving…"
            : editingCrId
              ? "Resubmit"
              : "Save changes"}
        </Button>
      </div>

    </div>
  );
}
